/**
 * 统一的分页配置工具
 * 提供标准化的分页配置，支持自定义每页显示条数
 */

import type { TablePaginationConfig } from 'antd/es/table';

export interface PaginationConfigProps {
  current?: number;
  pageSize?: number;
  total?: number;
  onChange?: (page: number, pageSize: number) => void;
  onShowSizeChange?: (current: number, size: number) => void;
  showSizeChanger?: boolean;
  showQuickJumper?: boolean;
  showTotal?: (total: number) => string;
  hideOnSinglePage?: boolean;
  size?: 'small' | 'default';
  pageSizeOptions?: string[];
  showSizeInput?: boolean; // 是否启用自定义输入（自动添加当前 pageSize 到选项）
}

/**
 * 创建虚拟滚动表格配置
 * @param height 表格固定高度，默认 450
 * @returns 虚拟滚动和滚动配置对象
 */
export const createVirtualScrollConfig = (height: number = 450) => ({
  virtual: true,
  scroll: { y: height, x: 'max-content' },
});

/**
 * 默认分页选项（包含大数值选项）
 * 最大值限制为 2000
 */
export const DEFAULT_PAGE_SIZE_OPTIONS = [
  '10',
  '20',
  '50',
  '100',
  '200',
  '500',
  '1000',
  '2000',
];

/**
 * 创建标准分页配置
 * @param config 分页配置参数
 * @returns 分页配置对象，如果没有总数则返回 false（不显示分页）
 */
export const createPaginationConfig = (
  config: PaginationConfigProps,
): TablePaginationConfig | false => {
  const {
    current,
    pageSize,
    total,
    onChange,
    onShowSizeChange,
    showSizeChanger = true,
    showQuickJumper = true,
    showTotal = (total) => `共 ${total} 条`,
    hideOnSinglePage = true,
    size = 'small',
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    showSizeInput = true, // 默认启用自定义输入
  } = config;

  // 如果没有总数，不显示分页
  if (total === undefined || total === 0) {
    return false;
  }

  // 构建 pageSizeOptions
  const options = [...pageSizeOptions];

  // 如果启用了 showSizeInput 且当前 pageSize 不在选项中，自动添加
  if (showSizeInput && pageSize) {
    const pageSizeStr = String(pageSize);
    if (!options.includes(pageSizeStr)) {
      options.push(pageSizeStr);
      // 排序：按数值大小排序
      options.sort((a, b) => Number(a) - Number(b));
    }
  }

  // 🔥 解决切换 pageSize 时调用两次接口的问题
  // antd Table 在切换 pageSize 时会同时触发 onChange 和 onShowSizeChange
  // 这里统一处理：onChange 负责翻页，onShowSizeChange 负责切换 pageSize 并重置到第一页
  const handlePageChange = (page: number, size?: number) => {
    // 如果只是翻页（size 未变或等于当前 pageSize），调用 onChange
    const finalSize = size ?? pageSize ?? 10; // 默认值为 10
    if (!size || size === pageSize) {
      onChange?.(page, finalSize);
    }
    // 如果 size 变了，说明是切换 pageSize，由 onShowSizeChange 处理
  };

  const handleSizeChange = (current: number, size: number) => {
    // 切换 pageSize 时，重置到第一页
    if (size !== pageSize) {
      onShowSizeChange?.(current, size);
    }
  };

  return {
    current,
    pageSize,
    total,
    showSizeChanger,
    showQuickJumper,
    showTotal,
    hideOnSinglePage,
    onChange: handlePageChange,
    onShowSizeChange: handleSizeChange,
    size,
    pageSizeOptions: options,
  };
};
